import * as yup from 'yup';
import { ALL_UNITS } from '@/features/units/schema';

export const ingredientSchema = yup.object({
  name: yup.string().required('Ingredient name is required'),
  subcategory_id: yup.string(),
  kcal: yup.number().typeError('Kcal must be a number').min(0, 'Kcal must be at least 0').required('Kcal is required'),
  protein: yup.number().typeError('Protein must be a number').min(0, 'Protein must be at least 0').required('Protein is required'),
  fat: yup.number().typeError('Fat must be a number').min(0, 'Fat must be at least 0').required('Fat is required'),
  carbs: yup.number().typeError('Carbs must be a number').min(0, 'Carbs must be at least 0').required('Carbs is required'),
  fiber: yup.number().typeError('Fiber must be a number').min(0, 'Fiber must be at least 0').required('Fiber is required'),
  density: yup.number().typeError('Density must be a number').min(0.0001, 'Density must be at least 0').required('Density is required'),
  piece_weight: yup.number().typeError('Piece weight must be a number').min(0, 'Piece weight must be at least 0').nullable(),
  slice_weight: yup.number().typeError('Slice weight must be a number').min(0, 'Slice weight must be at least 0').nullable(),
  package_size: yup.number().typeError('Package size must be a number').min(0, 'Package size must be at least 0').nullable(),
  portion_size: yup.number().typeError('Portion size must be a number').min(0, 'Portion size must be at least 0').nullable(),
  common: yup.boolean().default(false),
  preferred_unit: yup.string().oneOf(ALL_UNITS).required('Preferred unit is required'),
});

export function newIngredient() {
  return {
    name: '',
    subcategory_id: null,
    kcal: 0,
    protein: 0,
    fat: 0,
    carbs: 0,
    fiber: 0,
    density: 1,
    piece_weight: null,
    slice_weight: null,
    package_size: null,
    portion_size: null,
    common: false,
    preferred_unit: 'g',
  };
}

// Empty column means no explicit sort (URL has no sort arg); ingredients still
// fall back to name/asc when sorting.
export function newIngredientSort() {
  return { column: '', direction: 'asc' };
}

// Bulk edit pairs every editable field with a checkbox saying whether to touch
// it at all. The checkbox lives in the form as `enabled_<key>`, and a field is
// only validated — and only sent — when its checkbox is on.
export const BULK_EDIT_NUMERIC_FIELDS = [
  { key: 'kcal', label: 'Kcal' },
  { key: 'protein', label: 'Protein (g)' },
  { key: 'fat', label: 'Fat (g)' },
  { key: 'carbs', label: 'Carbs (g)' },
  { key: 'fiber', label: 'Fiber (g)' },
  { key: 'density', label: 'Density (g/ml)' },
  { key: 'piece_weight', label: 'Piece Weight (g)' },
  { key: 'slice_weight', label: 'Slice Weight (g)' },
  { key: 'package_size', label: 'Package Size (g)' },
  { key: 'portion_size', label: 'Portion Size (g)' },
];

export const BULK_EDIT_KEYS = [
  'subcategory_id',
  'preferred_unit',
  'common',
  ...BULK_EDIT_NUMERIC_FIELDS.map((field) => field.key),
];

export function enabledName(key) {
  return `enabled_${key}`;
}

export const BULK_EDIT_ENABLED_NAMES = BULK_EDIT_KEYS.map(enabledName);

function onlyWhenEnabled(key, validate) {
  return yup.mixed().when(enabledName(key), {
    is: true,
    then: validate,
    otherwise: (schema) => schema.strip(),
  });
}

export const bulkEditIngredientsSchema = yup.object({
  // Not stripped: the `when` conditions below read these as siblings during the
  // cast, so they have to survive it. `bulkEditChanges` drops them afterwards.
  ...Object.fromEntries(BULK_EDIT_ENABLED_NAMES.map((name) => [name, yup.boolean().default(false)])),

  subcategory_id: onlyWhenEnabled('subcategory_id', () =>
    yup.number().typeError('Pick a category').required('Pick a category')),

  preferred_unit: onlyWhenEnabled('preferred_unit', () =>
    yup.string().oneOf(ALL_UNITS, 'Pick a unit').required('Pick a unit')),

  common: onlyWhenEnabled('common', () => yup.boolean().required()),

  ...Object.fromEntries(BULK_EDIT_NUMERIC_FIELDS.map(({ key, label }) => [
    key,
    onlyWhenEnabled(key, () =>
      yup.number()
        .typeError(`${label} must be a number`)
        .min(0, `${label} must be at least 0`)
        .required(`${label} is required`)),
  ])),
});

// Turns validated form values into the request body: the checkboxes are form
// state only, and subcategory_id goes over the wire as a string.
export function bulkEditChanges(values) {
  const changes = Object.fromEntries(
    Object.entries(values).filter(([key]) => !BULK_EDIT_ENABLED_NAMES.includes(key))
  );

  if (changes.subcategory_id != null) {
    changes.subcategory_id = String(changes.subcategory_id);
  }

  return changes;
}

export function newBulkIngredientEdit() {
  return {
    ...Object.fromEntries(BULK_EDIT_ENABLED_NAMES.map((name) => [name, false])),
    subcategory_id: null,
    preferred_unit: 'g',
    common: false,
    ...Object.fromEntries(BULK_EDIT_NUMERIC_FIELDS.map((field) => [field.key, 0])),
  };
}

export function newIngredientFilters() {
  return {
    categories: [],
    subcategories: [],
    preferredUnits: [],
    commonOnly: false,
    kcal: null,
    protein: null,
    fat: null,
    carbs: null,
    fiber: null,
    density: null,
    piece_weight: null,
    slice_weight: null,
    package_size: null,
    portion_size: null,
  };
}
